import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import {
  IAllLocationGroupsResponse,
  ILocationGroup,
  ILocationGroupAvailabilitiesResponse,
  ILocationGroupAvailabilityResponse,
} from "../models/LocationGroup";

export default function useLocationGroups() {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const locationGroups = useQuery({
    queryKey: ["location-groups"],
    queryFn: () => get<IAllLocationGroupsResponse>("/locationgroup"),
    staleTime: 1000 * 60 * 10, //10 minutes
  });

  const availabilities = useQuery({
    queryKey: ["location-group-availabilities"],
    queryFn: () => get<ILocationGroupAvailabilitiesResponse>("/locationgroup/availabilities"),
    staleTime: 1000 * 60 * 2, //2 minutes
  });

  const availability = (locationGroupId: string) =>
    get<ILocationGroupAvailabilityResponse>(`/locationgroup/${locationGroupId}/availability`);

  const create = useMutation(
    (locationGroup: ILocationGroup) => post("/locationgroup", locationGroup),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["location-groups"]);
        queryClient.invalidateQueries(["location-group-availabilities"]);
      },
    }
  );

  const update = useMutation(
    (locationGroup: ILocationGroup) => put(`/locationgroup/${locationGroup.id}`, locationGroup),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["location-groups"]);
        queryClient.invalidateQueries(["location-group-availabilities"]);
      },
    }
  );

  const remove = useMutation(
    (locationGroupId: string) => del(`/locationgroup/${locationGroupId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["location-groups"]);
        queryClient.invalidateQueries(["location-group-availabilities"]);
      },
    }
  );

  return {
    locationGroups,
    availabilities,
    availability,
    create,
    update,
    remove,
  };
}
